@if (loading) {
  <div class="loading-wrap"><mat-progress-spinner mode="indeterminate" diameter="40" /></div>
} @else if (task) {

  <!-- ── Gradient header (type-keyed) ─────────────────────────────────────── -->
  <div class="detail-header type-header--{{ task.task_type }}">
    <div class="header-left">
      <span class="type-badge">
        <mat-icon>{{ typeIcon(task.task_type) }}</mat-icon>
        {{ typeLabel(task.task_type) }}
      </span>
      <span class="task-key">{{ task.task_key }}</span>
    </div>
    <div class="header-actions">
      @if (data.isProjectMember) {
        <button class="hdr-btn" (click)="openEdit()" matTooltip="Edit task">
          <mat-icon>edit</mat-icon>
        </button>
      }
      <button class="hdr-btn" (click)="close()" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <div class="detail-body">

    <!-- ── Left: main content ────────────────────────────────────────────── -->
    <div class="main-col">

      <h2 class="task-title">{{ task.title }}</h2>

      @if (task.description) {
        <div class="description">{{ task.description }}</div>
      } @else {
        <p class="no-desc">No description added yet.</p>
      }

      <!-- Acceptance Criteria -->
      @if (task.acceptance_criteria) {
        <div class="section ac-section">
          <div class="section-title">
            <span class="ac-badge">✓</span>
            Acceptance Criteria
          </div>
          <div class="ac-body">{{ task.acceptance_criteria }}</div>
        </div>
      }

      <!-- Labels -->
      @if (task.labels?.length) {
        <div class="labels-row">
          @for (label of task.labels; track label) {
            <span class="label-chip">{{ label }}</span>
          }
        </div>
      }

      <!-- Subtasks -->
      @if (task.subtasks?.length) {
        <div class="section">
          <div class="section-title">
            <mat-icon>format_list_bulleted</mat-icon>
            Subtasks
            <span class="section-count">{{ doneSubtasks() }}/{{ task.subtasks!.length }}</span>
          </div>
          <div class="progress-track">
            <div class="progress-fill" [style.width.%]="subtaskProgress()"></div>
          </div>
          @for (sub of task.subtasks; track sub.id) {
            <div class="subtask-row status--{{ sub.status }}">
              <span class="subtask-dot status--{{ sub.status }}"></span>
              <span class="subtask-key">{{ sub.task_key }}</span>
              <span class="subtask-title">{{ sub.title }}</span>
              <span class="subtask-status-label status--{{ sub.status }}">{{ statusLabel(sub.status) }}</span>
              <span class="subtask-assignee">{{ sub.assignee ? sub.assignee.first_name : '—' }}</span>
            </div>
          }
        </div>
      }

      <div class="section-divider"></div>

      <!-- Comments -->
      <div class="section" #commentsSection>
        <div class="section-title">
          <mat-icon>chat_bubble_outline</mat-icon>
          Comments
          <span class="section-count">{{ task.comments?.length ?? 0 }}</span>
        </div>

        @for (comment of task.comments; track comment.id) {
          <div class="comment">
            <div class="comment-avatar">
              @if (comment.author.profile_picture) {
                <img [src]="comment.author.profile_picture" referrerpolicy="no-referrer" />
              } @else {
                <span class="avatar-initial">{{ initial(comment.author.first_name) }}</span>
              }
            </div>
            <div class="comment-bubble">
              <div class="comment-meta">
                <span class="comment-author">{{ comment.author.first_name }} {{ comment.author.last_name }}</span>
                <span class="comment-time">{{ comment.created_at | date:'MMM d, h:mm a' }}</span>
                @if (canEditComment(comment)) {
                  <button class="comment-action" (click)="startEditComment(comment)">Edit</button>
                  <button class="comment-action comment-action--danger" (click)="deleteComment(comment)">Delete</button>
                }
              </div>
              @if (editCommentId === comment.id) {
                <div class="edit-comment-row">
                  <textarea class="comment-textarea" [formControl]="editCommentCtrl" rows="2"></textarea>
                  <div class="edit-actions">
                    <button class="cancel-btn" (click)="cancelEditComment()">Cancel</button>
                    <button class="save-btn" (click)="saveEditComment(comment)">Save</button>
                  </div>
                </div>
              } @else {
                <p class="comment-text">{{ comment.body }}</p>
              }
            </div>
          </div>
        }

        <!-- New comment — members only -->
        @if (data.isProjectMember) {
          <div class="new-comment">
            <div class="new-comment-inner">
              <textarea class="comment-textarea new-comment-textarea" [formControl]="commentCtrl"
                        rows="2" placeholder="Write a comment…"></textarea>
              <button class="submit-btn" [disabled]="commentCtrl.invalid || savingComment"
                      (click)="submitComment()">
                @if (savingComment) {
                  <mat-progress-spinner mode="indeterminate" diameter="14" />
                } @else {
                  <mat-icon>send</mat-icon>
                }
              </button>
            </div>
          </div>
        }
      </div>

      <!-- Activity -->
      @if (task.activities?.length) {
        <div class="section-divider"></div>
        <div class="section">
          <div class="section-title">
            <mat-icon>history</mat-icon>
            Activity
          </div>
          <div class="activity-feed">
            @for (act of task.activities; track act.id) {
              <div class="activity-row">
                <span class="activity-dot"></span>
                <span class="act-user">{{ act.user.first_name }}</span>
                changed <strong>{{ act.field }}</strong>
                @if (act.old_value) { from <em>{{ activityLabel(act.field, act.old_value) }}</em> }
                to <em>{{ activityLabel(act.field, act.new_value) }}</em>
                <span class="act-time">· {{ act.created_at | date:'MMM d, h:mm a' }}</span>
              </div>
            }
          </div>
        </div>
      }

    </div>

    <!-- ── Right: metadata sidebar ────────────────────────────────────────── -->
    <div class="meta-col type-meta--{{ task.task_type }}">

      <div class="meta-group">
        <div class="meta-label">Status</div>
        <span class="status-pill status--{{ task.status }}">
          <mat-icon>{{ statusIcon(task.status) }}</mat-icon>
          {{ statusLabel(task.status) }}
        </span>
      </div>

      <div class="meta-group">
        <div class="meta-label">Priority</div>
        <span class="priority-pill priority--{{ task.priority }}">
          <mat-icon>{{ priorityIcon(task.priority) }}</mat-icon>
          {{ priorityLabel(task.priority) }}
        </span>
      </div>

      @if (task.story_points !== null) {
        <div class="meta-group">
          <div class="meta-label">Story Points</div>
          <div class="points-badge">{{ task.story_points }}</div>
        </div>
      }

      <div class="meta-group">
        <div class="meta-label">Assignee</div>
        @if (task.assignee) {
          <div class="meta-user">
            @if (task.assignee.profile_picture) {
              <img class="meta-avatar" [src]="task.assignee.profile_picture" referrerpolicy="no-referrer" />
            } @else {
              <span class="meta-avatar meta-avatar--initial">{{ initial(task.assignee.first_name) }}</span>
            }
            <span>{{ task.assignee.first_name }} {{ task.assignee.last_name }}</span>
          </div>
        } @else {
          <span class="meta-empty">Unassigned</span>
        }
      </div>

      <div class="meta-group">
        <div class="meta-label">Reporter</div>
        <div class="meta-user">
          @if (task.reporter.profile_picture) {
            <img class="meta-avatar" [src]="task.reporter.profile_picture" referrerpolicy="no-referrer" />
          } @else {
            <span class="meta-avatar meta-avatar--initial">{{ initial(task.reporter.first_name) }}</span>
          }
          <span>{{ task.reporter.first_name }} {{ task.reporter.last_name }}</span>
        </div>
      </div>

      @if (task.due_date) {
        <div class="meta-group">
          <div class="meta-label">Due Date</div>
          <div class="due-date-val">
            <mat-icon>event</mat-icon>
            {{ fmtDate(task.due_date) }}
          </div>
        </div>
      }

      <div class="meta-group">
        <div class="meta-label">Created</div>
        <span class="meta-val">{{ task.created_at | date:'MMM d, y' }}</span>
      </div>

    </div>
  </div>
}
